/**
 * Sizing for long-text field editors — TRUS-496.
 *
 * Maui LTRG's imported Asana case notes run to ~33,000 characters for a single
 * member (the longest is ~65,000). Those fields were rendering in the same fixed
 * four-row box as a one-line "notes" field, so reading a case history meant
 * scrolling a ~90px window for pages. The reporter asked for "extra long text
 * boxes that are intended for very long notes and are readable without as much
 * scrolling ... for all field configuration across all organizations".
 *
 * A field already declares how much text it expects — `com_fields.str_len` — so
 * the editor is sized from that rather than from a new per-field setting nobody
 * would have to fill in. The box then grows with its actual content up to a cap,
 * so a long field holding two lines doesn't reserve half a screen.
 */

/** Starting rows for a long-text editor, graduated by the field's configured cap. */
export function longTextRows(strLen?: number | null): number {
  const cap = strLen ?? 0;
  if (cap >= 50000) return 16; // narrative history (imported case notes)
  if (cap >= 10000) return 10; // long notes
  return 4; // the previous fixed height, kept for ordinary long text
}

/**
 * Tallest an auto-growing editor may become, in pixels. Beyond this the textarea
 * scrolls internally — the point is to remove most scrolling, not to let one
 * field push everything else off the page.
 */
export const LONG_TEXT_MAX_HEIGHT_PX = 640;

/**
 * Grow a textarea to fit its content, up to {@link LONG_TEXT_MAX_HEIGHT_PX}.
 *
 * Height is reset to 'auto' first so the element can also SHRINK when text is
 * deleted; reading scrollHeight without that reset only ever ratchets upward.
 */
export function autoGrowTextarea(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = 'auto';
  const next = Math.min(el.scrollHeight, LONG_TEXT_MAX_HEIGHT_PX);
  el.style.height = `${next}px`;
  el.style.overflowY = el.scrollHeight > LONG_TEXT_MAX_HEIGHT_PX ? 'auto' : 'hidden';
}
